﻿<script setup lang="ts">
    import { reactive, inject, onMounted } from 'vue';
    import List from '@shared/components/List.vue';
    import { type tabs } from '@shared/components/ListTypes';
    import ListItem from './ListItem.vue';
    import { type TextMessage } from './TextMessagesTypes';
    import {
        loadDrafts,
        loadSent,
        loadStatisticsCount,
        loadScheduled,
        loadPaused,
        loadSending,
        deleteSelected,
        abortSending,
        changeStatus,
    } from './TextMessages.access';
    import { bsAlert } from '@shared/components/bsAlerts';
    import { ScopeAccess, vRequireScopeParams } from '@shared/directives/Scope.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import PrePaid from './PrePaid.vue';
    import { Permissions } from '@shared/Root/Permissions';

    export type status = 'drafts' | 'scheduled' | 'paused' | 'sending' | 'sent';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);

    const state = reactive<{
        items: TextMessage[];
        isBusy: boolean;
        currentTab: status;
        tabData: null | {
            description: string;
        };
        selected: string[];
        hasScope: boolean;
    }>({
        items: [],
        isBusy: false,
        currentTab: 'drafts',
        tabData: null,
        selected: [],
        hasScope: false,
    });

    const tabs: tabs = {
        drafts: { name: 'drafts', display: $Resources.SmsDraftsTab },
        scheduled: { name: 'scheduled', display: $Resources.SmsScheduledTab, hidden: true },
        paused: { name: 'paused', display: $Resources.SmsPausedTab, hidden: true },
        sending: { name: 'sending', display: $Resources.SmsSendingTab, hidden: true },
        sent: { name: 'sent', display: $Resources.SmsSentTab },
    };

    onMounted(async () => {
        const requireScopeParams: vRequireScopeParams = {
            Feature: 'Sms',
            Permission: Permissions.Write,
        };

        state.hasScope = scopeAccess.hasScope(requireScopeParams);

        const location = window.location;
        const url = new URL(location.toString());
        const params = url.searchParams;

        params.get('sent') && bsAlert({ title: $Resources.AlertSuccessHeading, content: $Resources.AlertSmsScheduledForSendingBody });
        params.get('draftrestored') && bsAlert({ title: $Resources.AlertSmsAbortedHeader, content: $Resources.AlertSmsAbortedBody });

        loadStatisticsCount().then((data) => {
            data.Paused && (tabs.paused.hidden = false);
            data.Scheduled && (tabs.scheduled.hidden = false);
        });

        switchTab((params.get('tab') as status) || 'drafts');
    });

    const switchTab = async (tab: status) => {
        if (state.isBusy) return;

        clearSelection();

        state.currentTab = tab;
        state.isBusy = true;
        state.items = [];

        state.tabData = {
            description: $Resources['Sms' + state.currentTab.charAt(0).toUpperCase() + state.currentTab.slice(1) + 'Introduction'],
        };

        /*
        $Resources.SmsScheduledIntroduction
        $Resources.SmsPausedIntroduction
        $Resources.SmsSendingIntroduction
        $Resources.SmsSentIntroduction
        $Resources.SmsDraftsIntroduction
        */

        loadTextMessages(state.currentTab);
    };

    const load: { [key in status]: any } = {
        drafts: loadDrafts,
        scheduled: loadScheduled,
        paused: loadPaused,
        sending: loadSending,
        sent: loadSent,
    };

    let sendingTimeout: Timeout;

    const loadTextMessages = async (type: status) => {
        await load[type]()
            .then((data: TextMessage[]) => {
                console.log('TextMessages', data);
                state.items = data;

                clearTimeout(sendingTimeout);

                if (type === 'sending' && data.length > 0) {
                    sendingTimeout = setTimeout(function () {
                        switchTab('sending');
                    }, 5000);
                }
            })
            .finally(() => (state.isBusy = false));
    };

    const filterOptions = () => {
        return ['Subject', 'Category.Name'];
    };

    const orderOptions = () => {
        return [
            { display: $Resources.smsSortByLastModifiedLabel, key: 'Modified' },
            { display: $Resources.smsSortBySubjectLabel, key: 'Subject' },
            { display: $Resources.SortByCategoryLabel, key: 'Category.Name' },
        ];
    };

    const onDeleteSelected = () => {
        if (state.isBusy) return;
        state.isBusy = true;

        let list = state.selected.map((id: string) => {
            return {
                SmsMessageId: id,
            };
        });

        deleteSelected(list)
            .then(() => {
                state.selected.forEach((id: string) => {
                    let found = state.items.find((item) => {
                        return id === item.SmsMessageId;
                    });

                    found && (found.Deleted = true);
                });

                bsAlert({ title: $Resources.AlertSmsDeletedHeading, content: $Resources.AlertSmsDeletedBody });
            })
            .catch(() => {
                bsAlert({ type: 'danger', title: $Resources.ErrorDeletingSmsHeading, content: $Resources.ErrorDeletingSmsBody });
            })
            .finally(() => {
                state.isBusy = false;
            });
    };

    const clearSelection = () => {
        state.selected = [];
    };

    const filteredItems = () => {
        return state.items.filter((item) => {
            return !item.Deleted;
        });
    };

    const onChangeStatus = (data: { id: string; status: 3 | 10 }) => {
        if (state.isBusy) return;
        state.isBusy = true;

        changeStatus(data.id, data.status).finally(() => {
            state.isBusy = false;
            switchTab('drafts');
        });
    };
</script>

<script lang="ts">
    export const onAbortSending = (id: string, isBusy: boolean, cb?: () => {}) => {
        if (isBusy) return;
        isBusy = true;

        abortSending(id)
            .then(() => {
                bsAlert({ title: $Resources.AlertSmsAbortedHeader, content: $Resources.AlertSmsAbortedBody });
            })
            .catch(function () {
                bsAlert({ type: 'danger', title: $Resources.ErrorAbortingSmsHeader, content: $Resources.ErrorAbortingSmsBody });
            })
            .finally(() => {
                isBusy = false;
            });
    };
</script>

<template>
    <List
        v-if="state.tabData"
        itemId="SmsMessageId"
        :title="$Resources.SmsMenuItem"
        :description="state.tabData.description"
        :items="filteredItems()"
        :tabs="tabs"
        :currentTab="state.currentTab"
        :loading="state.isBusy"
        :filterKeys="filterOptions()"
        :orderOptions="orderOptions()"
        :checkedItems="state.selected"
        :disableCheckboxes="state.currentTab === 'paused' || state.currentTab === 'scheduled' || state.currentTab === 'sending'"
        @update:tab="(newVal) => switchTab(newVal as status)"
        @update:checkedItems="(newVal) => (state.selected = newVal as string[])"
        @clearSelected="clearSelection"
        @deleteSelected="onDeleteSelected">
        <template #header>
            <PrePaid></PrePaid>
        </template>
        <template #toolbar>
            <router-link v-if="state.hasScope" :to="{ name: 'NewTextMessage' }" class="btn btn-default" v-show="state.selected.length === 0">
                <span class="glyphicon glyphicon-plus m-xxs-r"></span>
                {{ $Resources.SmsNewDraftButton }}
            </router-link>
        </template>
        <template #item="{ item, index }">
            <ListItem
                :textMessage="item"
                :currentTab="state.currentTab"
                @abortSending="(id) => onAbortSending(id, state.isBusy, () => switchTab('drafts'))"
                @update:status="(data) => onChangeStatus(data)"></ListItem>
        </template>
    </List>
</template>

<style lang="less"></style>

<style scoped lang="less"></style>
